import { useEffect, useRef, useState } from "react";
import {
  fetchDrBoyMachineData,
  hasDrBoyMachineData,
} from "./fetch-machine-data-dr-boy";
import type {
  DrBoyMachineDataResponse,
  DrBoyMachineOverviewDefinition,
  DrBoyMachineState,
} from "@src/types/factory-dashboard-dr-boy";
import type { ModeColor, ModeConfig } from "@src/types/factory-dashboard";
import { getQueryLastTimeseriesByMetadataUseCase } from "@src/cloud-connect/use-cases/get-query-last-timeseries-by-metadata";
import { querySourceByIdUseCase } from "@src/cloud-connect/client-configuration/use-cases/query-source-by-id-use-case";
import store from "@src/reducers/store";
import { useI18N } from "@src/language/language-context";
import { formatTextValue } from "../../../cloud-connect/utils/format-text-value";

const FALLBACK_QUERY_PARAMS = {
  limit: 1,
  duration: "PT30M",
  limit_order: "DESC" as const,
};

const createEmptyMachineState = (time = ""): DrBoyMachineState => ({
  machineMode: { value: null, time },
  job: { value: null, time: "" },
  nominalParts: { value: null, time: "" },
  jobGoodPartsCounter: { value: null, time: "" },
  cycleTime: { value: null, time: "" },
  jobBadPartsCounter: { value: null, time: "" },
});

const extractFallbackTimestamp = (data: unknown): string => {
  if (!data || typeof data !== "object") {
    return "";
  }

  const maybeEntry = data as {
    values?: Array<{ time?: string }>;
    time?: string;
  };

  return maybeEntry.values?.[0]?.time ?? maybeEntry.time ?? "";
};

const getLatestTimestamp = (first = "", second = ""): string => {
  if (!first) {
    return second;
  }

  if (!second) {
    return first;
  }

  const firstMs = Date.parse(first);
  const secondMs = Date.parse(second);

  if (Number.isNaN(firstMs)) {
    return second;
  }

  if (Number.isNaN(secondMs)) {
    return first;
  }

  return firstMs >= secondMs ? first : second;
};

const parseNumericNodeValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = typeof value === "number" ? value : Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export interface UseMachineOverviewDrBoyResult {
  machineTitle: string;
  isLoading: boolean;
  hasData: boolean;
  modeLabel: string;
  modeColor: ModeColor;
  job: string;
  jobGoodPartsCounter: number | null;
  nominalParts: number | null;
  cycleTime: number | null;
  progress: number | null;
  animatedProgressPercentage: number;
  timeRemainingHours: number | null;
  effectivity: number | null;
  lastUpdatedTime: string;
}

export const useMachineOverviewDrBoy = (
  machine: DrBoyMachineOverviewDefinition,
): UseMachineOverviewDrBoyResult => {
  const i18n = useI18N();
  const { sourceId, nodes, modeMapping } = machine;
  const [machineState, setMachineState] = useState<DrBoyMachineState | null>(
    null,
  );
  const [machineTitle, setMachineTitle] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [animatedProgressPercentage, setAnimatedProgressPercentage] =
    useState(0);

  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    const loadMachineName = async () => {
      const namespace =
        store.getState().yourSlice.authContext?.customerNamespace;

      if (!namespace) {
        setMachineTitle("");
        return;
      }

      const sourceInfo = await querySourceByIdUseCase(sourceId, false);
      setMachineTitle(sourceInfo?.data?.name ?? "");
    };

    loadMachineName().catch((err) => {
      console.error(
        `Error fetching source info for DR BOY machine ${sourceId}:`,
        err,
      );
      setMachineTitle("");
    });
  }, [sourceId]);

  useEffect(() => {
    const loadMachineData = async () => {
      if (isInitialLoadRef.current) {
        setIsLoading(true);
      }

      try {
        const res: DrBoyMachineDataResponse = await fetchDrBoyMachineData(
          sourceId,
          nodes,
        );
        const [, state] = Object.entries(res)[0] ?? [];

        if (hasDrBoyMachineData(state)) {
          setMachineState(state);
          return;
        }

        const [machineModeFallbackResponse, cycleTimeFallbackResponse] =
          await Promise.all([
            getQueryLastTimeseriesByMetadataUseCase({
              group: sourceId,
              source: nodes.machineMode,
              ...FALLBACK_QUERY_PARAMS,
            }),
            getQueryLastTimeseriesByMetadataUseCase({
              group: sourceId,
              source: nodes.cycleTime,
              ...FALLBACK_QUERY_PARAMS,
            }),
          ]);

        if (
          !machineModeFallbackResponse.success &&
          !cycleTimeFallbackResponse.success
        ) {
          console.error(
            `Failed to load fallback timestamps for DR BOY machine ${sourceId}`,
          );
        }

        const machineModeFallbackTimestamp = machineModeFallbackResponse.success
          ? extractFallbackTimestamp(machineModeFallbackResponse.data)
          : "";
        const cycleTimeFallbackTimestamp = cycleTimeFallbackResponse.success
          ? extractFallbackTimestamp(cycleTimeFallbackResponse.data)
          : "";

        const fallbackTimestamp = getLatestTimestamp(
          machineModeFallbackTimestamp,
          cycleTimeFallbackTimestamp,
        );

        setMachineState(createEmptyMachineState(fallbackTimestamp));
      } catch (err) {
        console.error(
          `Error fetching DR BOY data for machine ${sourceId}:`,
          err,
        );
      } finally {
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
          setIsLoading(false);
        }
      }
    };

    loadMachineData();

    const intervalId = setInterval(loadMachineData, 5000);

    return () => clearInterval(intervalId);
  }, [nodes, sourceId]);

  const hasData =
    machineState !== null &&
    Object.values(machineState).some((v) => v?.value != null);

  const job = formatTextValue(machineState?.job?.value);
  const jobGoodPartsCounter = parseNumericNodeValue(
    machineState?.jobGoodPartsCounter?.value,
  );
  const nominalParts = parseNumericNodeValue(machineState?.nominalParts?.value);
  const cycleTime = parseNumericNodeValue(machineState?.cycleTime?.value);
  const jobBadPartsCounter = parseNumericNodeValue(
    machineState?.jobBadPartsCounter?.value,
  );
  const lastUpdatedTime = getLatestTimestamp(
    machineState?.machineMode?.time ?? "",
    machineState?.cycleTime?.time ?? "",
  );

  const progress =
    nominalParts !== null && jobGoodPartsCounter !== null
      ? nominalParts > 0
        ? (jobGoodPartsCounter / nominalParts) * 100
        : 0
      : null;
  const progressPercentage =
    progress === null ? 0 : Math.max(0, Math.min(progress, 100));

  const timeRemainingHours =
    cycleTime !== null && nominalParts !== null && jobGoodPartsCounter !== null
      ? cycleTime > 0
        ? (cycleTime * Math.max(0, nominalParts - jobGoodPartsCounter)) / 3600
        : 0
      : null;

  const effectivity =
    jobGoodPartsCounter !== null && jobBadPartsCounter !== null
      ? jobGoodPartsCounter + jobBadPartsCounter > 0
        ? (jobGoodPartsCounter / (jobGoodPartsCounter + jobBadPartsCounter)) *
          100
        : 0
      : null;

  useEffect(() => {
    if (isLoading) {
      setAnimatedProgressPercentage(0);
      return;
    }

    setAnimatedProgressPercentage(0);

    const animationFrame = window.requestAnimationFrame(() => {
      setAnimatedProgressPercentage(progressPercentage);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [isLoading, progressPercentage]);

  const modeValue = Number(machineState?.machineMode?.value ?? 0);
  const mode = modeMapping[modeValue] as ModeConfig | undefined;

  const modeLabel = mode?.label ? i18n(mode.label) : "";
  const modeColor = mode?.color ?? { outline: "", background: "" };

  return {
    machineTitle,
    isLoading,
    hasData,
    modeLabel,
    modeColor,
    job,
    jobGoodPartsCounter,
    nominalParts,
    cycleTime,
    progress,
    animatedProgressPercentage,
    timeRemainingHours,
    effectivity,
    lastUpdatedTime,
  };
};
